import { useTranslation } from "react-i18next";
import { assetService } from "../../Core/Api/services";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { PermissionType } from "../../Core/Api/Types/permission-type";
import { IsPermissionGranted } from "../../Core/Utils/asset-object-utils";
import GlobalConst from "../../Core/Utils/Constants/globals";
import {
  AddChildrenToTreeObj,
  refreshTree,
  RemoveChildrenFromTreeObj,
  TreeContext,
} from "../../Redux/Reducers/redux-slice-tree";
import { useAppDispatch } from "../../Redux/redux-store";
import { UserHasAcls } from "../helpers";
import useCustomSnackbar from "./useCustomSnackbar";

export const AssetParentMatrix: { [key in ObjectLayer]: ObjectLayer[] } = {
  Head: [],
  Title: [],
  Content: [], //[ObjectLayer.Title],
  SubContent: [],
  Group: [ObjectLayer.Content, ObjectLayer.Group],
  Asset: [ObjectLayer.Content, ObjectLayer.Group],
  Clip: [],
};

const useAssetTreeManipulation = () => {
  const enqueueSnackbar = useCustomSnackbar();
  const dispatch = useAppDispatch();

  const getIntersectLayerOfSelection = (selection: DbAssetObjectInfos[]): ObjectLayer[] => {
    let output: ObjectLayer[] = [];
    for (let i = 0; i < selection.length; i++) {
      if (i === 0) output = AssetParentMatrix[selection[i]!.layer];
      output = output.filter((layer) => AssetParentMatrix[selection[i]!.layer].includes(layer));
    }
    return output;
  };

  // Create empty group
  const canCreateGroupInAsset = (asset: DbAssetObjectInfos): boolean => {
    return AssetParentMatrix[ObjectLayer.Group].includes(asset.layer);
  };
  const { t } = useTranslation();
  const createGroup = (
    groupName: string,
    parentAsset: DbAssetObjectInfos,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => {
    if (!canCreateGroupInAsset(parentAsset)) {
      enqueueSnackbar(t("alert:warning.creationIsNotAllowedOnThisLayer"), "warning");
      return;
    }

    const newObject = new DbAssetObjectInfos({
      type: ObjectType.Group,
      classification: ObjectClass.Group,
      layer: ObjectLayer.Group,
      parentObjectId: parentAsset.id,
      name: groupName,
      hidden: false,
      isDeleted: false,
    });

    assetService
      .CreateStandalone({
        objs: [newObject],
      })
      .then((response) => {
        if (response.createdObjects && response.createdObjects.length) {
          successCallback && successCallback();
          enqueueSnackbar(t("alert:success.createdGroupWithId", { id: response.createdObjects[0].id }), "success");
          dispatch(
            AddChildrenToTreeObj({
              parentId: parentAsset.id,
              children: response.createdObjects,
              context: TreeContext.AssetHierarchy,
            })
          );
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(response.message || t("alert:error.unableToCreateGroup"), "error");
        }
      })
      .catch((error) => {
        errorCallback && errorCallback();
        enqueueSnackbar(error.message || t("alert:error.unableToCreateGroup"), "error");
      });
  };
  // Delete group(s)

  const canDeleteGroups = (groups: DbAssetObjectInfos[]): boolean => {
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      let countChilds = group.childObjects !== null ? group.childObjects.length : 0;

      if (countChilds > 0 || !IsPermissionGranted(group, PermissionType.Trash)) return false;
    }

    return true;
  };

  const deleteGroup = (group: DbAssetObjectInfos, successCallback?: () => void, errorCallback?: () => void) => {
    if (!canDeleteGroups([group])) {
      enqueueSnackbar(t("alert:warning.notHavePermissionToDeleteGroup"), "warning");
      return;
    }

    const groupId = group.id;
    assetService
      .DeleteObjectById(groupId)
      .then((response) => {
        if (response.success) {
          successCallback && successCallback();
          enqueueSnackbar(t("alert:success.createdGroupWithId", { id: groupId }), "success");
          dispatch(
            RemoveChildrenFromTreeObj({
              children: [groupId],
              context: TreeContext.AssetHierarchy,
            })
          );
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(response.message || t("alert:error.unableToDeleteGroup", { id: groupId }), "error");
        }
      })
      .catch((error) => {
        errorCallback && errorCallback();
        enqueueSnackbar(error.message || t("alert:error.unableToDeleteGroup", { id: groupId }), "error");
      });
  };

  const deleteGroups = (groups: DbAssetObjectInfos[], successCallback?: () => void, errorCallback?: () => void) => {
    if (!canDeleteGroups(groups)) {
      enqueueSnackbar(t("alert:warning.notHavePermissionToDeleteAtLastOneInGroup"), "warning");
      return;
    }

    const groupsIds = groups.map((asset) => asset.id);
    if (!groupsIds.length) {
      enqueueSnackbar(t("alert:warning.noGroupSelected"), "warning");
      return;
    }
    assetService
      .DeleteObjectsById(groupsIds)
      .then((response) => {
        if (response) {
          successCallback && successCallback();
          enqueueSnackbar(t("alert:success.deletedGroup"), "success");
          dispatch(
            RemoveChildrenFromTreeObj({
              children: groupsIds,
              context: TreeContext.AssetHierarchy,
            })
          );
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(t("alert:warning.unableToDeleteAllGroups"), "warning");
        }
      })
      .catch(() => {
        errorCallback && errorCallback();
        enqueueSnackbar(t("alert:warning.unableToDeleteAllGroups"), "warning");
      });
  };
  // Create new group with assets
  const canCreateGroupOfSelection = (selection: DbAssetObjectInfos[]): boolean => {
    const intersect = getIntersectLayerOfSelection(selection);
    return AssetParentMatrix[ObjectLayer.Group].filter((layer) => intersect.includes(layer)).length > 0;
  };

  const groupAssets = (
    parentAsset: DbAssetObjectInfos,
    groupName: string,
    childAssetIds: number[],
    successCallback?: () => void,
    errorCallback?: () => void
  ) => {
    if (!canCreateGroupInAsset(parentAsset)) {
      enqueueSnackbar(t("alert:warning.creationIsNotAllowedOnThisLayer"), "warning");
      return;
    }

    assetService
      .GroupAssets({ assetName: groupName, parentAssetId: parentAsset.id, childAssetIds: childAssetIds })
      .then((response) => {
        if (response.success) {
          successCallback && successCallback();
          enqueueSnackbar(t("alert:success.createdGroupWithId", { id: response.objectId }), "success");
          dispatch(refreshTree({ context: TreeContext.AssetHierarchy, currentId: parentAsset.id }));
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(response.message || t("alert:error.somethingWentWrongWithDot"), "error");
        }
      })
      .catch((error) => {
        errorCallback && errorCallback();
        enqueueSnackbar(error.message || t("alert:error.somethingWentWrongWithDot"), "error");
      });
  };

  // Move assets
  const canMoveSelection = (selection: DbAssetObjectInfos[]): boolean => {
    return (
      getIntersectLayerOfSelection(selection).length > 0 &&
      UserHasAcls([
        AclTypes.aclMetadataMoveLinkContentUpdate,
        AclTypes.aclMetadataMoveLinkTitleUpdate,
        AclTypes.aclElevationWishlistCreate,
      ])
    );
  };

  const moveSelectionToAsset = (
    objectsIdsToMove: number[],
    parentId: number,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => {
    if (!objectsIdsToMove.length) {
      enqueueSnackbar(t("alert:warning.selectOneObjectToMove"), "warning");
      return;
    }
    assetService
      .MoveObjectAsync({ objectIds: objectsIdsToMove, targetParentObjectId: parentId })
      .then((response) => {
        if (response.success) {
          successCallback && successCallback();
          if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
            enqueueSnackbar(response.message!, "primary");
          else {
            enqueueSnackbar(t("alert:success.movedAssetsToParent", { id: parentId }), "success");
            dispatch(refreshTree({ context: TreeContext.AssetHierarchy, currentId: parentId }));
            dispatch(refreshTree({ context: TreeContext.MoveDistrib, currentId: parentId }));
          }
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(response.message || t("alert:error.somethingWentWrongWithDot"), "error");
        }
      })
      .catch((error) => {
        errorCallback && errorCallback();
        enqueueSnackbar(error.message || t("alert:error.somethingWentWrongWithDot"), "error");
      });
  };
  // Copy assets
  const canCopySelection = (selection: DbAssetObjectInfos[]): boolean => {
    return (
      getIntersectLayerOfSelection(selection).length > 0 &&
      UserHasAcls([AclTypes.aclMetadataMoveLinkContentUpdate, AclTypes.aclElevationWishlistCreate])
    );
  };

  const copySelectionToAsset = (
    objectsIdsToCopy: number[],
    providerId: number,
    parentId: number,
    placeholderTemplateId?: number,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => {
    if (!objectsIdsToCopy.length) {
      enqueueSnackbar(t("alert:warning.selectOneObjectToCopy"), "warning");
      return;
    }

    assetService
      .CopyObjectAsync({
        objectIds: objectsIdsToCopy,
        providerId: providerId,
        targetParentObjectId: parentId,
        overridePlaceholderTemplateId: placeholderTemplateId,
      })
      .then((response) => {
        if (response.success) {
          successCallback && successCallback();
          enqueueSnackbar(t("alert:success.copiedAssetsToParent", { id: parentId }), "success");
          dispatch(refreshTree({ context: TreeContext.AssetHierarchy, currentId: parentId }));
          dispatch(refreshTree({ context: TreeContext.MoveDistrib, currentId: parentId }));
        } else if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID) {
          successCallback && successCallback();
          enqueueSnackbar(response.message!, "primary");
        } else {
          errorCallback && errorCallback();
          enqueueSnackbar(response.message || t("alert:error.somethingWentWrongWithDot"), "error");
        }
      })
      .catch((error) => {
        errorCallback && errorCallback();
        enqueueSnackbar(error.message || t("alert:error.somethingWentWrongWithDot"), "error");
      });
  };

  return {
    // util
    getIntersectLayerOfSelection,
    // create empty group
    canCreateGroupInAsset,
    createGroup,
    // Delete group(s)
    canDeleteGroups,
    deleteGroup,
    deleteGroups,
    // group assets
    canCreateGroupOfSelection,
    groupAssets,
    // move assets
    canMoveSelection,
    moveSelectionToAsset,
    // copy assets
    canCopySelection,
    copySelectionToAsset,
  };
};

export default useAssetTreeManipulation;
